// src/components/modals/WeeklyScheduleModal.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAvailability } from '../../contexts/AvailabilityContext';

const DAYS_OF_WEEK = [
  { key: 'sunday',    label: 'ראשון',  short: 'א׳' },
  { key: 'monday',    label: 'שני',    short: 'ב׳' },
  { key: 'tuesday',   label: 'שלישי',  short: 'ג׳' },
  { key: 'wednesday', label: 'רביעי',  short: 'ד׳' },
  { key: 'thursday',  label: 'חמישי',  short: 'ה׳' },
  { key: 'friday',    label: 'שישי',   short: 'ו׳' },
  { key: 'saturday',  label: 'שבת',    short: 'ש׳' },
];

const DAY_INDEX_BY_KEY = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

const FALLBACK_TIME_SLOTS = [
  '08:00','09:00','10:00','11:00','12:00','13:00',
  '14:00','15:00','16:00','17:00','18:00','19:00',
  '20:00','21:00','22:00',
];

const FIXED_LESSON_MINUTES = 50;
const MAX_CONSECUTIVE_LESSONS = 4;

/* ── small reusable pieces ─────────────────────────────────── */
const SectionLabel = ({ color = 'indigo', children }) => {
  const colors = {
    indigo: 'from-indigo-500 to-violet-600',
    emerald: 'from-emerald-500 to-teal-500',
  };
  return (
    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2 uppercase tracking-wide">
      <span className={`w-[3px] h-4 rounded-full bg-gradient-to-b ${colors[color]} flex-shrink-0`} />
      {children}
    </h3>
  );
};

const FieldLabel = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
    {children}
  </label>
);

const selectCls = (hasError) =>
  `w-full h-10 rounded-xl border px-3 text-sm bg-white text-gray-800
   focus:outline-none focus:ring-2 transition-all duration-150
   ${hasError
     ? 'border-red-300 bg-red-50 focus:ring-red-200'
     : 'border-gray-200 hover:border-indigo-300 focus:ring-indigo-200 focus:border-indigo-400'
   }`;

/* ── main component ────────────────────────────────────────── */
const WeeklyScheduleModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialSchedule = null,
  courseTitle = 'קורס',
  teacherBusySlots = [],
}) => {
  const [schedule, setSchedule] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [selectedEndTime, setSelectedEndTime] = useState('');
  const [lessonsCount, setLessonsCount] = useState(1);
  // No longer user-editable (the "הגדרות נוספות" section was removed);
  // still sent to onSubmit so the saved scheduleMetadata keeps its shape.
  const repeatWeeks = 12;
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);

  const availabilityCtx = useAvailability();
  const availabilityGrid = availabilityCtx?.availability || null;
  const availabilityTimeSlots = availabilityCtx?.timeSlots || null;

  const TIME_SLOTS = useMemo(() => {
    if (Array.isArray(availabilityTimeSlots) && availabilityTimeSlots.length) {
      return availabilityTimeSlots.map((slot) => {
        const raw = slot?.start || '';
        if (!raw) return null;
        const [hRaw, mRaw = '00'] = String(raw).split(':');
        return `${String(hRaw).padStart(2,'0')}:${String(mRaw).padStart(2,'0')}`;
      }).filter(Boolean);
    }
    return FALLBACK_TIME_SLOTS;
  }, [availabilityTimeSlots]);

  const toMinutes = (t) => { if (!t) return 0; const [h,m]=t.split(':').map(Number); return (h||0)*60+(m||0); };
  const hasOverlap = (s1,e1,s2,e2) => toMinutes(s1)<toMinutes(e2)&&toMinutes(s2)<toMinutes(e1);
  const computeEnd = (start, blocks=1) => {
    if (!start) return '';
    const total = toMinutes(start) + blocks * FIXED_LESSON_MINUTES;
    return `${String(Math.floor(total/60)).padStart(2,'0')}:${String(total%60).padStart(2,'0')}`;
  };
  const fmtDuration = (min) => {
    const h=Math.floor(min/60), m=min%60;
    if (h&&m) return `${h}ש׳ ${m}ד׳`;
    if (h) return `${h} ${h===1?'שעה':'שעות'}`;
    return `${m} דק׳`;
  };
  const weeklyHours = () => (schedule.reduce((s,x)=>s+(x.duration||FIXED_LESSON_MINUTES),0)/60).toFixed(1);

  const handleClose = () => {
    setErrors({});
    setSelectedDay(''); setSelectedStartTime(''); setSelectedEndTime(''); setLessonsCount(1);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key==='Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow=prev; window.removeEventListener('keydown',onKey); };
  }, [isOpen]);

  useEffect(() => {
    if (initialSchedule && Array.isArray(initialSchedule)) {
      setSchedule(initialSchedule.map((s,i)=>({
        id: s.id??Date.now()+i, ...s,
        duration: s.duration||(s.lessons||1)*FIXED_LESSON_MINUTES,
        lessons: s.lessons||1,
      })));
    } else { setSchedule([]); }
  }, [initialSchedule]);

  const normalizedBusy = useMemo(() =>
    (teacherBusySlots||[]).map((b,i)=>({
      id: b.id??`busy-${i}`, day: b.day, startTime: b.startTime,
      endTime: b.endTime||computeEnd(b.startTime, b.lessons||1),
    }))
  , [teacherBusySlots]);

  const isRangeFree = (dayKey, startTime, blocks=1) => {
    if (!dayKey||!startTime) return false;
    const endTime = computeEnd(startTime, blocks);
    const conflict = [...schedule.filter(s=>s.day===dayKey), ...normalizedBusy.filter(s=>s.day===dayKey)]
      .some(slot=>hasOverlap(slot.startTime,slot.endTime,startTime,endTime));
    if (conflict) return false;
    const dayIndex = DAY_INDEX_BY_KEY[dayKey];
    const startIdx = TIME_SLOTS.indexOf(startTime);
    if (availabilityGrid&&Array.isArray(availabilityGrid)&&dayIndex>=0&&startIdx>=0) {
      for (let k=0;k<blocks;k++) {
        const idx=startIdx+k;
        if (!availabilityGrid[dayIndex]||idx>=availabilityGrid[dayIndex].length||!availabilityGrid[dayIndex][idx]) return false;
      }
    }
    return true;
  };

  const availableStartTimes = useMemo(() =>
    !selectedDay ? TIME_SLOTS : TIME_SLOTS.filter(t=>isRangeFree(selectedDay,t,1))
  , [selectedDay,TIME_SLOTS,schedule,normalizedBusy,availabilityGrid]);

  const availableLessonCounts = useMemo(() => {
    if (!selectedDay||!selectedStartTime) return Array.from({length:MAX_CONSECUTIVE_LESSONS},(_,i)=>i+1);
    const startIdx=TIME_SLOTS.indexOf(selectedStartTime);
    if (startIdx===-1) return [1];
    const counts=[];
    for (let b=1;b<=MAX_CONSECUTIVE_LESSONS;b++) {
      if (startIdx+b-1>=TIME_SLOTS.length) break;
      if (isRangeFree(selectedDay,selectedStartTime,b)) counts.push(b);
    }
    return counts.length?counts:[1];
  }, [selectedDay,selectedStartTime,TIME_SLOTS,schedule,normalizedBusy,availabilityGrid]);

  useEffect(() => {
    if (!availableLessonCounts.includes(lessonsCount)) {
      const next=availableLessonCounts[0]||1;
      setLessonsCount(next);
      if (selectedDay&&selectedStartTime) setSelectedEndTime(computeEnd(selectedStartTime,next));
    }
  }, [availableLessonCounts,lessonsCount,selectedDay,selectedStartTime]);

  const addTimeSlot = () => {
    const errs = {};
    if (!selectedDay) errs.day='יש לבחור יום';
    if (!selectedStartTime) errs.startTime='יש לבחור שעת התחלה';
    if (!errs.day&&!errs.startTime) {
      if (!availableLessonCounts.includes(lessonsCount)) errs.conflict='אין מספיק שיעורים רצופים פנויים';
      else if (!isRangeFree(selectedDay,selectedStartTime,lessonsCount)) errs.conflict='קיימת חפיפה עם מפגש קיים';
    }
    if (Object.keys(errs).length) { setErrors(errs); return; }
    const start=selectedStartTime, end=computeEnd(start,lessonsCount);
    setSchedule(prev=>[...prev,{
      id:Date.now(), day:selectedDay,
      dayLabel:DAYS_OF_WEEK.find(d=>d.key===selectedDay)?.label,
      startTime:start, endTime:end,
      duration:lessonsCount*FIXED_LESSON_MINUTES, lessons:lessonsCount,
    }].sort((a,b)=>{
      const d=DAYS_OF_WEEK.findIndex(x=>x.key===a.day)-DAYS_OF_WEEK.findIndex(x=>x.key===b.day);
      return d||a.startTime.localeCompare(b.startTime);
    }));
    setSelectedStartTime(''); setSelectedEndTime(''); setLessonsCount(1); setErrors({});
  };

  const removeTimeSlot = (id) => setSchedule(prev=>prev.filter(s=>s.id!==id));

  const handleSubmit = async () => {
    if (submitting) return;
    if (!schedule.length) { setErrors({general:'יש להוסיף לפחות מפגש אחד'}); return; }
    setSubmitting(true);
    let ok = true;
    try {
      const res = await onSubmit?.({
      weekly: schedule.map(({id,...rest})=>rest),
      sessionDuration: FIXED_LESSON_MINUTES, repeatWeeks,
      totalSessions: schedule.length*repeatWeeks,
      weeklyHours: weeklyHours(),
      });
      ok = res !== false;
    } catch (e) {
      ok = false;
    } finally {
      setSubmitting(false);
    }
    // Save first, close only after it finished (stay open on failure).
    if (ok) handleClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (!modalRef.current?.contains(e.target)) handleClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" />

      {/* Modal */}
      <div
        ref={modalRef}
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
        className="relative z-[1] w-full sm:max-w-2xl lg:max-w-3xl bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/25
                   flex flex-col max-h-[96dvh] sm:max-h-[90vh]
                   mx-0 sm:mx-4"
      >
        {/* ── Header ── */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 rounded-t-3xl px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            {/* icon */}
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="18" rx="3"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="7" y1="2" x2="7" y2="6"/>
                <line x1="17" y1="2" x2="17" y2="6"/>
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">מערכת שעות שבועית</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">הגדרת מפגשים קבועים עבור {courseTitle}</p>
            </div>
          </div>
          {/* close */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
            aria-label="סגור"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-5">

          {/* ── Add slot card ── */}
          <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-4">
              <SectionLabel color="emerald">הוסף מפגש חדש</SectionLabel>
            </div>

            {/* Fields grid — 2 cols on mobile, 4 on md */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Day */}
              <div>
                <FieldLabel>יום</FieldLabel>
                <select
                  value={selectedDay}
                  onChange={(e) => {
                    setSelectedDay(e.target.value);
                    setSelectedStartTime(''); setSelectedEndTime(''); setLessonsCount(1); setErrors({});
                  }}
                  className={selectCls(errors.day)}
                >
                  <option value="">בחר יום</option>
                  {DAYS_OF_WEEK.map(d=>(
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </select>
                {errors.day && <p className="mt-1 text-[11px] text-red-500 font-medium">{errors.day}</p>}
              </div>

              {/* Start time */}
              <div>
                <FieldLabel>שעת התחלה</FieldLabel>
                <select
                  value={selectedStartTime}
                  disabled={!selectedDay}
                  onChange={(e) => {
                    const v=e.target.value;
                    setSelectedStartTime(v);
                    setSelectedEndTime(v?computeEnd(v,lessonsCount):'');
                    setErrors({});
                  }}
                  className={selectCls(errors.startTime)}
                >
                  <option value="">בחר שעה</option>
                  {availableStartTimes.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                {errors.startTime && <p className="mt-1 text-[11px] text-red-500 font-medium">{errors.startTime}</p>}
              </div>

              {/* Lessons count */}
              <div>
                <FieldLabel>שיעורים רצופים</FieldLabel>
                <select
                  value={lessonsCount}
                  disabled={!selectedDay||!selectedStartTime}
                  onChange={(e) => {
                    const n=Number(e.target.value)||1;
                    setLessonsCount(n);
                    if (selectedStartTime) setSelectedEndTime(computeEnd(selectedStartTime,n));
                    setErrors({});
                  }}
                  className={selectCls(false)}
                >
                  {(!selectedDay||!selectedStartTime)
                    ? <option value={1}>—</option>
                    : availableLessonCounts.map(n=>(
                        <option key={n} value={n}>{n} {n===1?'שיעור':'שיעורים'}</option>
                      ))
                  }
                </select>
              </div>

              {/* End time (readonly) */}
              <div>
                <FieldLabel>שעת סיום</FieldLabel>
                <div className={`h-10 rounded-xl border border-dashed border-gray-200 bg-white px-3 flex items-center text-sm ${selectedEndTime?'text-gray-700 font-medium':'text-gray-300'}`}>
                  {selectedEndTime || 'אוטומטי'}
                </div>
              </div>
            </div>

            {/* Conflict / error banner */}
            {(errors.conflict) && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-600 font-medium">
                <svg className="w-4 h-4 flex-shrink-0 mt-px" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 7a1 1 0 012 0v4a1 1 0 01-2 0V7zm1 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
                </svg>
                {errors.conflict}
              </div>
            )}

            {/* Add button */}
            <button
              type="button"
              onClick={addTimeSlot}
              disabled={!selectedDay||!selectedStartTime}
              className="mt-4 w-full sm:w-auto flex items-center justify-center gap-2 px-5 h-10 rounded-xl text-sm font-semibold transition-all duration-150
                         bg-emerald-500 text-white shadow-sm shadow-emerald-200 hover:bg-emerald-600 hover:shadow-md
                         disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none disabled:cursor-not-allowed"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
              הוסף מפגש
            </button>
          </div>

          {/* ── Schedule list ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <SectionLabel color="indigo">מפגשים שנקבעו</SectionLabel>
              {schedule.length > 0 && (
                <span className="text-xs text-gray-400 font-medium">
                  {schedule.length} מפגשים · {weeklyHours()} שעות/שבוע
                </span>
              )}
            </div>

            {schedule.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 rounded-2xl border-2 border-dashed border-gray-200 text-gray-300">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" className="mb-2">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="7" y1="2" x2="7" y2="6" stroke="currentColor" strokeWidth="1.5"/>
                  <line x1="17" y1="2" x2="17" y2="6" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <p className="text-sm">טרם נוספו מפגשים</p>
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
                {DAYS_OF_WEEK.map((day) => {
                  const slots = schedule.filter(s=>s.day===day.key);
                  if (!slots.length) return null;
                  return (
                    <div key={day.key} className="flex items-start gap-0 bg-white">
                      {/* Day pill */}
                      <div className="flex-shrink-0 w-20 sm:w-24 flex items-center justify-center self-stretch bg-indigo-50 border-l border-indigo-100">
                        <span className="text-xs font-bold text-indigo-600 tracking-wide">{day.label}</span>
                      </div>
                      {/* Slots */}
                      <div className="flex-1 flex flex-wrap gap-2 p-3">
                        {slots.map(slot=>(
                          <div
                            key={slot.id}
                            className="group flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                                       bg-white border border-indigo-200 shadow-sm shadow-indigo-50
                                       hover:border-indigo-400 hover:shadow-indigo-100 transition-all duration-150"
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="9" stroke="#6366f1" strokeWidth="1.8"/>
                              <path d="M12 7v5l3 3" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round"/>
                            </svg>
                            {/* dir="ltr": inside this RTL modal the range rendered visually
                                reversed (end–start); keep it start–end. */}
                            <span dir="ltr" className="text-xs font-semibold text-gray-700">{slot.startTime}–{slot.endTime}</span>
                            <span className="text-[10px] text-gray-400">({fmtDuration(slot.duration)})</span>
                            <button
                              type="button"
                              onClick={()=>removeTimeSlot(slot.id)}
                              className="mr-0.5 w-5 h-5 rounded-lg flex items-center justify-center
                                         text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all duration-150"
                              aria-label="הסר"
                            >
                              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* General error */}
          {errors.general && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-600 font-medium">
              <svg className="w-4 h-4 flex-shrink-0 mt-px" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 7a1 1 0 012 0v4a1 1 0 01-2 0V7zm1 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/>
              </svg>
              {errors.general}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                       hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
          >
            ביטול
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={schedule.length === 0 || submitting}
            className="h-10 px-6 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 text-white text-sm font-bold
                       shadow-md shadow-indigo-200 hover:shadow-lg hover:shadow-indigo-300 hover:-translate-y-px
                       disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0
                       transition-all duration-150 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
            </svg>
            {submitting ? 'שומר…' : 'שמור מערכת שעות'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WeeklyScheduleModal;